/**
 * The chat scaffold every phase of a knock is rendered in (M8–M12, PRD §5.3).
 *
 * A knock is one conversation from the moment the sender arrives: the intake is
 * asked in it, the grilling happens in it, and the standing lands at the foot of
 * it. So the column itself — the door header, the scrolling thread, the composer
 * pinned to the bottom — lives here, and both `KnockArea` (the intake) and
 * `KnockStartedScreen` (the grilling) render the same one. Nothing in this file
 * knows about the network or about a knock: it is shape, not state, which is how
 * the phases can hand over to each other without the sender seeing a new screen.
 *
 * Three voices, told apart by shape rather than by reading them: the doorman on
 * the left in the grey a landed message wears, the sender on the right in
 * near-black, and the room itself centred and quiet between them. Those two
 * fills are `bubble-them` and `bubble-me` — the palette's own names for
 * "arrived" and "sent", and the only place in the app that uses them.
 */
import {
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type ReactNode,
} from "react";
import type { Attachment, KnockMessage, MessageRole } from "../../lib/api";
import { linkify } from "../../lib/linkify";
import { runAt } from "../../lib/runs";
import { parseQuoted, type Quote } from "../../lib/quote";
import { QuotedPrefix, useBubbleMenu, type MenuTarget } from "./bubble-menu";
import { NobFace } from "../shell/mascot";
import type { WorkItemState } from "./work-indicator";

/**
 * One line of the conversation, already attributed. A line is usually words;
 * a sender's line can instead be a document (§6.3) — carried IN the thread, at
 * the moment it was attached, because a file the sender put into the
 * conversation is part of the conversation, not a sidebar to it. The thread
 * renders an attachment line as the attachment bubble and ignores `content`.
 */
export type ThreadLine = {
  role: MessageRole;
  content: string;
  attachment?: Attachment;
  /**
   * A piece of the doorman's work, drawn where it happened (wave 82): under
   * the doorman line that asked for it, above whatever it said once the work
   * came back. The thread renders a work line as the work indicator and
   * ignores `role` and `content`.
   */
  work?: WorkItemState;
  /**
   * When it was said, in milliseconds, for the runs that group bubbles (wave
   * 90, `lib/runs.ts`). Set on every line read back from the server; a line
   * drawn live before the re-read has none, which a run reads as "no gap".
   */
  at?: number | null;
};

/** A file as the sender's knock read carries it: with the moment it was put in. */
export type TimedAttachment = Attachment & { created_at?: string | null };

/** A piece of work as the sender's knock read carries it (`GET /knock/:id`'s `work`). */
export type TimedWork = WorkItemState & { created_at: string | null };

/**
 * The hearing read back: the transcript's words, the files the sender put in
 * (§6.3), and the work the doorman did — EACH WHERE IT HAPPENED (wave 82).
 *
 * The first UI review watched a thread change on reload: attachment chips
 * jumped from where they were sent to the foot of the conversation, and the
 * work lines under the doorman's replies vanished. A file is not a turn and
 * carries no sequence, but it does carry the moment it was put in, and so does
 * every message and every piece of work — so the three are merged on that
 * moment, which is the order the live thread drew them in. A line with no
 * moment keeps its place among its own kind and goes after the rest.
 *
 * A VOICE NOTE SENT AS A TURN IS ONE LINE (wave 76). The transcript names the
 * note its line was said in, so that line wears the note — one bubble, the
 * player with the words under it — and the note is not drawn a second time
 * among the files.
 */
export function transcriptLines(
  messages: readonly KnockMessage[],
  attachments: readonly TimedAttachment[],
  work: readonly TimedWork[] = [],
): ThreadLine[] {
  const byId = new Map(attachments.map((a) => [a.attachment_id, a]));
  const said = new Set<string>();
  const timed: { at: number; rank: number; line: ThreadLine }[] = [];
  const at = (iso: string | null | undefined): number => {
    const ms = iso ? Date.parse(iso) : NaN;
    return Number.isFinite(ms) ? ms : Infinity;
  };
  for (const m of messages) {
    const note = m.voice_attachment_id ? byId.get(m.voice_attachment_id) : undefined;
    if (note) said.add(note.attachment_id);
    const when = runAt(m.created_at);
    const line: ThreadLine = note
      ? { role: m.role, content: m.content, attachment: note, at: when }
      : { role: m.role, content: m.content, at: when };
    timed.push({ at: at(m.created_at), rank: 0, line });
  }
  for (const attachment of attachments) {
    if (said.has(attachment.attachment_id)) continue;
    const { created_at, ...file } = attachment;
    timed.push({
      at: at(created_at),
      rank: 1,
      line: { role: "sender", content: "", attachment: file, at: runAt(created_at) },
    });
  }
  for (const item of work) {
    const { created_at, ...state } = item;
    timed.push({ at: at(created_at), rank: 2, line: { role: "notice", content: "", work: state } });
  }
  // A stable sort: equal moments keep what came first, and a message stored
  // in the same millisecond as the work it asked for stays above it.
  return timed
    .map((t, i) => ({ ...t, i }))
    .sort((a, b) => a.at - b.at || a.rank - b.rank || a.i - b.i)
    .map((t) => t.line);
}

/**
 * How a line names its own speaker in a quotation.
 *
 * The name goes ON THE WIRE — it is part of the text the quote-reply sends and
 * part of what the doorman reads — so it has to be the name the two people in
 * this conversation would use, not a role from the schema. "Nob" is what the
 * doorman is called everywhere on this product; "You" is who the sender is to
 * themselves, and what they are quoting when they quote their own line back.
 */
export function speakerOf(role: MessageRole): string {
  if (role === "doorman") return "Nob";
  // The person behind the door has a name, but it is the THREAD's to know and
  // not a role's; the bubble that draws one passes it in directly. What must
  // never happen is this falling through to "You" and attributing the door
  // owner's words to the sender in a quotation.
  if (role === "recipient") return "Them";
  return "You";
}

/**
 * The intro lands as a conversation rather than as a page: each opening bubble
 * enters ~150ms after the one above it. A delay on an entrance animation, not a
 * gate on the content — everything is in the document from the first paint, so
 * nothing is ever withheld from a reader, a test, or a search.
 */
/** The opening lines arrive at TALKING speed — a beat apart, like the
 * doorman chat's exchanges, not a form materialising (owner note 2026-08-05). */
export const STAGGER_MS = 400;

export const JUMP_COPY = "Jump to latest";

/** The way back out of a door, as the header's chevron says it. */
export const BACK_LABEL = "Back";

/** Distance from the foot, in px, still counted as being at the bottom. */
const NEAR_BOTTOM = 48;

/**
 * Distance from the top, in px, past which the masthead minimises. Small, but
 * not zero: a trackpad's overscroll bounce reports single-pixel offsets, and a
 * title that flickered between its two sizes on a resting thread would be worse
 * than one that never moved.
 */
const OFF_TOP = 12;

/**
 * MAY THE MASTHEAD COLLAPSE? — and the answer is not "has the log moved".
 *
 * THE HEIGHT AT WHICH THERE IS NO ANSWER (owner, staging walk 2026-09-18).
 * Collapsing the masthead gives the scroller the masthead's collapse `delta`
 * back, so the log it holds is `delta` less overflowing than it was. When the
 * log overflowed by less than that, the collapse makes the content FIT: the
 * browser clamps `scrollTop` back under `OFF_TOP`, the masthead opens, the
 * content overflows again, and the pair oscillate for ever — the owner met it
 * after a couple of messages and could not reach the bottom of the last one.
 * No debounce touches it, because there is no resting state in that band to
 * settle into; one of the two states has to be refused.
 *
 * So the collapse is allowed only where it can SURVIVE itself: enough overflow
 * with the masthead open that `delta` of it is still left afterwards. Measured
 * open, always — see `ChatShell`, which adds `delta` back while it is shut.
 *
 * AND THE EXPANSION FALLS OUT OF THE SAME LINE rather than needing a rule of
 * its own. Once shut, `overflowExpanded` is `scrollTop`'s own overflow plus
 * `delta`, and `scrollTop` cannot exceed the overflow it is scrolling through —
 * so anything past `OFF_TOP` keeps it shut, and only a reader scrolling back up
 * to the top opens it. Clamping cannot force it any more.
 */
export function shouldCollapse({
  scrollTop,
  overflowExpanded,
  delta,
}: {
  scrollTop: number;
  /** `scrollHeight - clientHeight` of the log, as it stands with the masthead OPEN. */
  overflowExpanded: number;
  /** The masthead's open height less its shut one. Measured, never a constant. */
  delta: number;
}): boolean {
  return scrollTop > OFF_TOP && overflowExpanded > OFF_TOP + delta;
}

const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

/**
 * The field every composer step types into — one shape for every question.
 *
 * The fill, the edge, the resting lift and the focus glow are `.nob-composer`
 * in styles.css: the composer has rungs of its own in the palette, and its
 * focus state is a soft blue halo rather than the outline everything else
 * wears — a field somebody is mid-sentence in should not be shouting.
 */
export const CHAT_FIELD =
  "nob-composer min-w-0 flex-1 rounded-2xl px-4 py-2.5 text-[15px] " +
  "leading-relaxed text-text placeholder:text-fainter focus-visible:outline-none " +
  "disabled:opacity-50";

/**
 * The door, as a circle with its initial in it — what a door with no cached
 * picture shows (FR-3.8.2). A picture that could not be fetched is ABSENT, and
 * absent looks like this: never a broken image, never a fallback to whatever
 * URL the picture came from.
 */
export function Monogram({ name }: { name: string }) {
  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-raised
                 font-mono text-sm text-text-soft"
    >
      {initial}
    </span>
  );
}

/**
 * The door's picture, or the letter standing in for it.
 *
 * `avatar` is a path on OUR origin — the server hands out nothing else, and
 * `fetchPublicDoor` drops anything that is not one — so this `<img>` cannot
 * reach a third-party CDN however the payload was tampered with. Decorative:
 * the name is in the same header, one element along.
 */
export function DoorFace({ name, avatar }: { name: string; avatar?: string | null }) {
  if (!avatar) return <Monogram name={name} />;
  return (
    <img
      src={avatar}
      alt=""
      width={32}
      height={32}
      className="h-8 w-8 shrink-0 rounded-full bg-raised object-cover"
    />
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
      <path
        d="M12.5 4.5 7 10l5.5 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUp() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
      <path
        d="M10 15.5v-11M5.5 9 10 4.5 14.5 9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type HeaderProps = {
  /** The door's name, when it is known; the handle carries it alone if not. */
  name: string | null;
  handle: string;
  /**
   * The door's picture, as a path on our own origin, or null for the monogram
   * (FR-3.8.2). A header handed only a name and a handle — the doorman thread,
   * a knock still booting — has no picture to show and says so by omission.
   */
  avatar?: string | null;
  /**
   * One quiet word about the door, on the right of the header. Never a figure —
   * it carried the hearing price once, then a two-word difficulty chip. Both
   * are gone: a door has no price (ruling C3) and its difficulty is a SENTENCE
   * the server authors, which belongs in the thread rather than in a chip.
   */
  note?: string | null;
  /** A second quiet line under the name — how far into the grilling the sender is. */
  meta?: string | null;
  /** null: this thread IS the room — there is nowhere back to go. */
  onBack: (() => void) | null;
  backLabel?: string;
  /** The budget hairline, when the server asks for it (R6). */
  bar?: ReactNode;
};

/**
 * Whose door this is, kept in view for the whole conversation: the way out, the
 * door, and how hard it is. Sticky, because a thread scrolls and the question
 * of where you are standing does not.
 */
export function DoorHeader({
  name,
  handle,
  avatar = null,
  note = null,
  meta = null,
  onBack,
  backLabel = BACK_LABEL,
  bar = null,
}: HeaderProps) {
  return (
    <header className="sticky top-0 z-10 flex-none border-b border-border-soft bg-bg/95 backdrop-blur">
      <div className="flex h-14 items-center gap-3 px-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel}
            className={`-ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full
                        text-faint transition-colors duration-75 hover:text-text-soft ${FOCUS}`}
          >
            <Chevron />
          </button>
        )}

        <DoorFace name={name || handle} avatar={avatar} />

        <div className="min-w-0 flex-1 leading-tight">
          {name && <p className="truncate text-sm font-medium text-text">{name}</p>}
          <p className="truncate font-mono text-xs text-faint">@{handle}</p>
          {meta && (
            <p className="truncate font-mono text-[11px] tabular-nums text-fainter">{meta}</p>
          )}
        </div>

        {note && <p className="shrink-0 font-mono text-xs text-faint">{note}</p>}
      </div>
      {bar}
    </header>
  );
}

/**
 * The column: header, thread, composer. The thread is the only thing that
 * scrolls, and it follows new content the way a chat does — unless the reader
 * has scrolled up, which is a decision the screen must not overrule. When they
 * have, the way back down is offered rather than taken.
 */
export function ChatShell({
  header,
  masthead = null,
  logOverlay = null,
  composer = null,
  footer = null,
  overlay = null,
  fill = false,
  dropHandlers,
  children,
}: {
  header: ReactNode;
  /**
   * Take the height of whatever is holding this, rather than measuring it off
   * the viewport.
   *
   * The subtraction below (`100dvh` less the chrome) was right when a
   * conversation was a page under a tab bar. In the shell it is a pane in a
   * grid, and a column that computes its own height inside a flex parent is a
   * column that is wrong by exactly the height of the pane's header.
   */
  fill?: boolean;
  /**
   * A block that sits under the header and ABOVE the scroller — Home's console
   * and nothing else so far.
   *
   * It is a slot rather than the first child of the conversation because the
   * two behave oppositely: a child scrolls away with the thread, and a summary
   * that scrolls away is not a summary. As a `flex-none` sibling it keeps its
   * place while the conversation moves under it, and the scroller's `flex-1`
   * gives up exactly the height it takes — which is why nothing here needs to
   * know how tall it is.
   */
  masthead?: ReactNode;
  /**
   * Something laid over THE LOG — between the masthead and the composer, both
   * of which stay reachable under it. Home's Morning Nob and nothing else so
   * far (owner, 2026-08-26).
   *
   * A slot of its own rather than `overlay`, which covers the whole column
   * including the field somebody types in: this one is a card the conversation
   * runs behind, not a state the column is in. It is positioned against the
   * scroller's own box, so nothing here needs to know how tall the masthead is.
   */
  logOverlay?: ReactNode;
  composer?: ReactNode;
  /** One quiet line under the composer — the doorman chat's meter lives here. */
  footer?: ReactNode;
  /**
   * Something laid over the whole column — today, the drop target. Passed in
   * rather than owned: this file is shape, and what may be dropped on a
   * conversation is the conversation's business (§6.3).
   */
  overlay?: ReactNode;
  dropHandlers?: {
    onDragEnter: (event: ReactDragEvent) => void;
    onDragOver: (event: ReactDragEvent) => void;
    onDragLeave: (event: ReactDragEvent) => void;
    onDrop: (event: ReactDragEvent) => void;
  };
  children: ReactNode;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  /** The reader has scrolled up: new content must not yank them back down. */
  const [pinned, setPinned] = useState(false);
  /**
   * The log has moved off its own top. The masthead reads this to minimise
   * itself — see `RecapMasthead`. It is a separate fact from `pinned`, which is
   * about the BOTTOM of the log: a thread can be scrolled away from its title
   * and still be following its last line.
   */
  const [scrolled, setScrolled] = useState(false);
  const mastheadBox = useRef<HTMLDivElement>(null);
  /**
   * How much height the masthead gives back when it shuts — the quantity
   * `shouldCollapse` refuses to guess.
   *
   * NEITHER HALF IS A NUMBER IN THE STYLESHEET, which is why this is measured
   * rather than written down: the open padding is 8/10px against the shut one's
   * 6/6, but most of the difference is `.nob-prompts-list` going from its own
   * content height to zero, and the shut state also grows a count line. On this
   * console's own shelf the two nearly cancel — it can even come out NEGATIVE,
   * a masthead that gets taller when it minimises, which is stable and needs no
   * guard at all. A hard-coded delta would have invented an instability here
   * and missed the real one somewhere else.
   *
   * MEASURED ON A CLONE, and that is not fastidiousness. Shutting the real one
   * to read it back cannot work: `max-height` and `padding` are TRANSITIONED,
   * so a synchronous read after flipping the attribute returns the value the
   * transition is starting FROM — the open height, every time (measured: 148px
   * for a shelf whose shut height is 40). A node that has just been inserted
   * has no before-change style and therefore no transitions, so the clone
   * settles into the shut state immediately. The original is never touched,
   * never painted shut, and never lies.
   */
  const [delta, setDelta] = useState(0);
  const hasMasthead = Boolean(masthead);

  /**
   * …and re-measured whenever the masthead's own content changes, because its
   * open height is whatever the shelf happens to be holding: a briefing bar
   * that arrives, prompts that are answered, a door that opened overnight.
   */
  useEffect(() => {
    const el = mastheadBox.current;
    if (!el) return;
    const measure = () => {
      const parent = el.parentElement;
      if (!parent) return;
      const box = el.getBoundingClientRect();
      // A shelf already shut is measured at its shut height; the pair is only
      // meaningful from the open state, and the observer will call again.
      if (el.dataset["scrolled"] === "true") return;
      const clone = el.cloneNode(true) as HTMLElement;
      clone.dataset["scrolled"] = "true";
      clone.setAttribute("aria-hidden", "true");
      clone.style.position = "absolute";
      clone.style.left = "-9999px";
      clone.style.top = "0";
      clone.style.width = `${box.width}px`;
      clone.style.visibility = "hidden";
      clone.style.pointerEvents = "none";
      parent.appendChild(clone);
      const shut = clone.getBoundingClientRect().height;
      clone.remove();
      setDelta(Math.max(0, Math.round(box.height - shut)));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMasthead]);

  const toBottom = () => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Every render, not every list change: a streamed line grows a bubble without
  // adding one, and following it is the whole point.
  useEffect(() => {
    if (!pinned) toBottom();
  });

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    setPinned(el.scrollHeight - el.scrollTop - el.clientHeight > NEAR_BOTTOM);
    // The overflow AS IT WOULD BE WITH THE MASTHEAD OPEN, which is the only
    // frame of reference the decision is stable in: while it is shut the log
    // has exactly `delta` more room than the open state would leave it.
    const overflowExpanded = el.scrollHeight - el.clientHeight + (scrolled ? delta : 0);
    setScrolled(shouldCollapse({ scrollTop: el.scrollTop, overflowExpanded, delta }));
  };

  return (
    <div
      {...dropHandlers}
      // Mobile owes the fixed TabBar and the top bar their room (they are the
      // shell's, not this column's); ≥sm only the page header is above us.
      className={
        fill
          ? "relative mx-auto flex h-full min-h-0 w-full max-w-2xl flex-col"
          : "relative mx-auto flex h-[calc(100dvh-14rem)] min-h-[22rem] w-full max-w-2xl flex-col sm:h-[calc(100dvh-5.5rem)] sm:min-h-[26rem]"
      }
    >
      {header}
      {masthead && (
        <div
          ref={mastheadBox}
          className="nob-masthead flex-none"
          data-scrolled={scrolled ? "true" : "false"}
        >
          {masthead}
        </div>
      )}
      {overlay}

      {/* The log and whatever floats over it, in one positioned box: an overlay
          anchored to the column would have to be told how tall the masthead is,
          and would cover the composer besides. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {logOverlay}
        <div
          ref={scroller}
          onScroll={onScroll}
          data-testid="chat-scroller"
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 py-5"
        >
          {/* `mt-auto` anchors a short conversation to the composer it is had
              with — a chat grows upward from where you type, and a gulf between
              the last bubble and the field reads as two unrelated regions. Once
              the thread outgrows the viewport this is inert and it scrolls as
              ever. */}
          <div className="mt-auto flex flex-col gap-3">{children}</div>
        </div>
      </div>

      {pinned && (
        <div className="pointer-events-none absolute inset-x-0 bottom-24 z-20 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setPinned(false);
              toBottom();
            }}
            className={`pointer-events-auto rounded-full border border-border-strong bg-surface/95 px-3 py-1.5
                        text-xs text-text-soft shadow-lg transition-colors duration-75
                        hover:border-border-loud ${FOCUS}`}
          >
            {JUMP_COPY}
          </button>
        </div>
      )}

      {(composer || footer) && (
        <div className="flex-none border-t border-border-soft bg-bg px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          {composer}
          {footer}
        </div>
      )}
    </div>
  );
}

function Row({
  children,
  end = false,
  delay = 0,
  gutter,
}: {
  children: ReactNode;
  end?: boolean;
  delay?: number;
  /** See `Gutter`: absent is no gutter at all, null is the gutter with no face in it. */
  gutter?: ReactNode | null;
}) {
  return (
    <div
      className={`nob-enter flex ${end ? "justify-end" : "justify-start"} ${
        gutter !== undefined ? "items-end gap-2" : ""
      }`}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {gutter !== undefined && <Gutter face={gutter} />}
      {children}
    </div>
  );
}

/**
 * THE FACE BESIDE A BUBBLE (wave 90). Every bubble of a run from somebody other
 * than the reader sits behind this one gutter, so the text column lines up; the
 * speaker's face stands in it beside the run's LAST bubble only, at the bottom
 * (`lib/runs.ts`). 28px, and 24px on a phone. The reader's own bubbles have no
 * gutter at all.
 */
export function Gutter({ face }: { face: ReactNode | null }) {
  return (
    <div data-face-gutter aria-hidden className="flex w-6 shrink-0 sm:w-7">
      {face}
    </div>
  );
}

/** Nob's face, at the gutter's size. */
export function NobBubbleFace() {
  return <NobFace size="bubble" mood="watching" alt="" />;
}

/**
 * The gesture handlers a bubble carries when there is a menu to open, and
 * nothing at all when there is not.
 *
 * `onMenu` being optional is what keeps every existing caller working and, more
 * to the point, is what keeps the menu OFF the bubbles that have nothing to
 * offer: a system row is not somebody speaking, and a streaming bubble is a
 * sentence still being written.
 */
type Menuable = {
  onMenu?: (target: MenuTarget) => void;
  /** The words this bubble would quote. Defaults to what it is rendering. */
  quoteText?: string;
};

function useMaybeMenu(onMenu: Menuable["onMenu"], quote: Quote) {
  const handlers = useBubbleMenu(onMenu ?? (() => undefined), quote);
  return onMenu ? handlers : {};
}

/** The doorman: left, in the pale grey a landed message wears. */
export function DoormanBubble({
  children,
  delay = 0,
  alert = false,
  onMenu,
  quoteText,
  gutter,
}: {
  children: ReactNode;
  delay?: number;
  /** A refusal or a stumble: said in the same voice, announced as it lands. */
  alert?: boolean;
  /** The face gutter (wave 90): Nob's face on a run's last bubble, null on the others. */
  gutter?: ReactNode | null;
} & Menuable) {
  const text = quoteText ?? (typeof children === "string" ? children : "");
  const menu = useMaybeMenu(onMenu, { speaker: "Nob", text });
  return (
    <Row delay={delay} gutter={gutter}>
      <div
        role={alert ? "alert" : undefined}
        data-bubble="them"
        {...menu}
        className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-md bg-bubble-them px-4
                   py-2.5 text-[15px] leading-relaxed text-bubble-them-fg
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
      >
        {speak(children)}
      </div>
    </Row>
  );
}

/**
 * THE WAIT, STANDING WHERE THE REPLY WILL (owner, staging walk 2026-09-18).
 *
 * It was a system footnote — centred, faint, footnote-sized, in the run of
 * quiet lines the room uses for its own asides — and the owner missed it on a
 * turn that took a minute and a half. A sentence that small, that grey and that
 * far from the conversation is indistinguishable from the room having nothing
 * to say, which is exactly the reading the line exists to prevent.
 *
 * So it is a BUBBLE, in the doorman's own place and size: the same left row,
 * the same fill, the same type as the words that are coming. The reply lands on
 * top of it — same column, same shape — so the wait turns into the answer in
 * place rather than one line vanishing and another appearing somewhere else.
 *
 * The animated ellipsis is what says the wait is live; `nob-breathe` is not
 * composed with it, because a bubble pulsing its whole opacity under three dots
 * doing the same thing is two clocks in one line. The dots are decoration and
 * are hidden from anyone not reading them visually — the sentence carries the
 * meaning on its own, and a reader who cannot see them must not be handed a
 * row of periods to spell out.
 */
export function ThinkingBubble({
  children,
  gutter,
}: {
  children: ReactNode;
  /** The face gutter (wave 90): the wait is Nob's bubble, so it wears Nob's face. */
  gutter?: ReactNode | null;
}) {
  return (
    <Row gutter={gutter}>
      <div
        data-bubble="them"
        data-testid="doorman-thinking"
        className="flex max-w-[85%] items-center rounded-2xl rounded-bl-md bg-bubble-them px-4
                   py-2.5 text-[15px] leading-relaxed text-bubble-them-fg"
      >
        {children}
        <i className="nob-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </i>
      </div>
    </Row>
  );
}

/**
 * THE PERSON BEHIND THE DOOR, once they have answered (owner, 2026-08-26).
 *
 * The third voice in what has always been a two-party thread, and it needs a
 * name where the other two do not: the sender knows who "you" is and has been
 * told all along that the other bubble is a Nob. This one is a stranger who has
 * just decided to talk to them, and an unattributed bubble in the doorman's
 * colours would read as the doorman changing its tone.
 *
 * It sits on the left with the Nob because that is where the far side of the
 * conversation lives, and it wears the accent tint rather than the doorman's
 * grey — the same paper the owner's own screen uses for the answers meant for
 * them, so "someone is speaking to you personally" looks the same on both
 * sides of the door.
 */
export function PersonBubble({
  name,
  children,
  delay = 0,
  onMenu,
  quoteText,
  gutter,
  label = true,
}: {
  name: string;
  children: ReactNode;
  delay?: number;
  /** The face gutter (wave 90): their face on a run's last bubble, null on the others. */
  gutter?: ReactNode | null;
  /** The name above the bubble — on a run's FIRST bubble only (wave 90). */
  label?: boolean;
} & Menuable) {
  const text = quoteText ?? (typeof children === "string" ? children : "");
  const menu = useMaybeMenu(onMenu, { speaker: name, text });
  return (
    <Row delay={delay} gutter={gutter}>
      <div className="flex max-w-[85%] flex-col gap-1 items-start">
        {label && (
          <span className="text-[11px] uppercase tracking-[0.08em] text-fainter">{name}</span>
        )}
        <div
          data-bubble="them"
          data-person="true"
          {...menu}
          className="whitespace-pre-wrap rounded-2xl rounded-bl-md border border-accent/40
                     bg-accent/10 px-4 py-2.5 text-[15px] leading-relaxed text-text
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
        >
          {speak(children)}
        </div>
      </div>
    </Row>
  );
}

/** The sender: right, and in near-black — the colour your own words wear. */
export function SenderBubble({
  children,
  delay = 0,
  onMenu,
  quoteText,
}: { children: ReactNode; delay?: number } & Menuable) {
  const text = quoteText ?? (typeof children === "string" ? children : "");
  const menu = useMaybeMenu(onMenu, { speaker: "You", text });
  return (
    <Row end delay={delay}>
      <div
        data-bubble="me"
        {...menu}
        className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-bubble-me px-4
                   py-2.5 text-[15px] leading-relaxed text-bubble-me-fg
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
      >
        {speak(children)}
      </div>
    </Row>
  );
}

/**
 * A bubble's contents, with any addresses in them made clickable.
 *
 * Only a bare string is touched. Everything else a bubble is handed — an
 * attachment row, a card — is already elements, and running a text transform
 * over those would be reaching into somebody else's render.
 */
function speak(children: ReactNode): ReactNode {
  return typeof children === "string" ? linkify(children) : children;
}

/** The room, not a speaker: centred, quiet, and never a bubble. */
export function SystemRow({
  children,
  breathe = false,
  ring = false,
  delay = 0,
}: {
  children: ReactNode;
  breathe?: boolean;
  /**
   * A turning ring before the line (owner, 2026-09-07). For a room notice the
   * knocker is WAITING ON rather than reading — the sender check, which is a
   * vendor lookup of about half a minute. `breathe` is the signal for a line
   * that is being written; this is the signal for a line that is holding, and
   * a row wants one of them, never both.
   */
  ring?: boolean;
  delay?: number;
}) {
  return (
    <p
      className={`nob-enter px-6 text-center text-xs leading-relaxed text-faint ${
        breathe ? "nob-breathe" : ""
      }`}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {ring ? <i className="nob-turn mr-1.5" aria-hidden="true" /> : null}
      {children}
    </p>
  );
}

/** Anything that needs answering inside the thread: money, a quote, a consent. */
export function ThreadCard({
  children,
  label,
}: {
  children: ReactNode;
  /** Names the card for anyone not reading it visually. */
  label?: string;
}) {
  return (
    <div
      className="nob-enter rounded-2xl border border-border bg-surface/40 p-4"
      {...(label ? { role: "group", "aria-label": label } : {})}
    >
      {children}
    </div>
  );
}

/**
 * One attributed line, in whichever of the three voices it belongs to.
 *
 * The quote a message carries is picked back off its text here rather than
 * being a field: `parseQuoted` reads the `> Someone wrote:` prefix the composer
 * wrote, and what is left is the reply. A message with no prefix comes through
 * untouched, which is every message this product has stored so far.
 *
 * The MENU quotes the whole message including its own quotation — quoting a
 * quote-reply quotes what that person said, not what they were answering.
 */
export function ChatLine({
  line,
  delay = 0,
  onMenu,
  personName,
  gutter,
  label,
}: {
  line: ThreadLine;
  delay?: number;
  onMenu?: (target: MenuTarget) => void;
  /**
   * The face gutter for a line from somebody other than the reader (wave 90):
   * their face on the last bubble of a run, null on the others. Absent draws
   * no gutter — the reader's own lines, and the room's.
   */
  gutter?: ReactNode | null;
  /** Whether a `recipient` line wears its name — the first of a run only. */
  label?: boolean;
  /**
   * What to call the person behind the door, for a `recipient` line. The thread
   * knows it (it is the door's own name) and a line does not, so it is handed
   * down rather than looked up. A door with no name falls back to "Them" — the
   * bubble must say who is speaking even when the record is thin.
   */
  personName?: string;
}) {
  if (line.role === "notice") return <SystemRow delay={delay}>{line.content}</SystemRow>;

  const { quote, body } = parseQuoted(line.content);
  const inner = quote ? (
    <>
      <QuotedPrefix quote={quote} />
      {linkify(body)}
    </>
  ) : (
    line.content
  );
  // Quoting this bubble quotes what was SAID in it, never the block it was
  // answering — otherwise a thread of replies quotes its own first message
  // forever.
  const quoteText = quote ? body : line.content;

  if (line.role === "sender") {
    return (
      <SenderBubble delay={delay} {...(onMenu ? { onMenu } : {})} quoteText={quoteText}>
        {inner}
      </SenderBubble>
    );
  }
  if (line.role === "recipient") {
    return (
      <PersonBubble
        name={personName?.trim() || "Them"}
        delay={delay}
        {...(onMenu ? { onMenu } : {})}
        quoteText={quoteText}
        gutter={gutter}
        {...(label === undefined ? {} : { label })}
      >
        {inner}
      </PersonBubble>
    );
  }
  return (
    <DoormanBubble
      delay={delay}
      {...(onMenu ? { onMenu } : {})}
      quoteText={quoteText}
      gutter={gutter}
    >
      {inner}
    </DoormanBubble>
  );
}

/** The composer's one button: send. Amber, round, and the only colour down here. */
export function SendButton({
  label = "Send",
  disabled = false,
}: {
  label?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      aria-label={label}
      disabled={disabled}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent
                  text-accent-fg transition-colors duration-75 hover:bg-accent-soft active:bg-accent-strong
                  disabled:pointer-events-none disabled:opacity-50 ${FOCUS}`}
    >
      <ArrowUp />
    </button>
  );
}

/** A composer step: a field, a send, and whatever quiet help sits under them. */
export function Composer({
  onSubmit,
  children,
  hint = null,
  above = null,
  sendLabel,
  disabled = false,
  ownsRefusal = false,
}: {
  onSubmit: () => void;
  children: ReactNode;
  hint?: ReactNode;
  /** Above the field: the quote being answered, and nothing else so far. */
  above?: ReactNode;
  sendLabel?: string;
  disabled?: boolean;
  /**
   * The step answers its own refusals, in the thread. Native constraint
   * validation would swallow the submit and answer with a browser tooltip
   * instead — which is exactly the red-mark-on-a-field this screen is not.
   */
  ownsRefusal?: boolean;
}) {
  return (
    <form
      noValidate={ownsRefusal}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {above}
      <div className="flex items-end gap-2">
        {children}
        <SendButton {...(sendLabel ? { label: sendLabel } : {})} disabled={disabled} />
      </div>
      {hint}
    </form>
  );
}

/** A quick amount, offered rather than typed. */
export function Chip({
  children,
  onClick,
  selected = false,
}: {
  children: ReactNode;
  onClick: () => void;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`rounded-full border px-3 py-1 font-mono text-xs tabular-nums transition-colors
                  duration-75 ${FOCUS} ${
                    selected
                      ? "border-accent/60 bg-accent/10 text-accent-ink-strong"
                      : "border-border text-muted hover:border-border-loud hover:text-text-soft"
                  }`}
    >
      {children}
    </button>
  );
}
